import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import CookieBanner from '../../components/feature/CookieBanner';

export default function EmpresaPage() {
  useEffect(() => {
    document.title = 'Sobre Nosotros - Empresa de Vaciado de Pisos en Vic | JUSTCLEAN VIC';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Conoce JUSTCLEAN VIC, empresa especializada en vaciado de pisos y locales en Vic con +10 años de experiencia. Equipo profesional y servicio discreto. ☎️ 602 36 59 96'
      );
    }

    // Add AboutPage Schema
    const aboutPageScript = document.createElement('script');
    aboutPageScript.type = 'application/ld+json';
    aboutPageScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'Sobre JUSTCLEAN VIC',
      description: 'Empresa especializada en vaciado de pisos y locales en Vic con más de 10 años de experiencia',
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/empresa`,
      mainEntity: {
        '@type': 'Organization',
        name: 'JUSTCLEAN VIC',
        url: import.meta.env.VITE_SITE_URL || 'https://example.com',
        logo: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/vite.svg`,
        description: 'Empresa líder en vaciado de pisos, locales y limpieza especializada en Vic y comarca de Osona',
        foundingDate: '2013',
        telephone: '+34602365996',
        email: 'info@vic.justclean.es',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Carrer de Jacint Verdaguer, 15',
          addressLocality: 'Vic',
          postalCode: '08500',
          addressRegion: 'Barcelona',
          addressCountry: 'ES',
        },
      },
    });
    document.head.appendChild(aboutPageScript);

    return () => {
      document.head.removeChild(aboutPageScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-secondary/10 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl sm:text-5xl font-bold text-dark mb-6">
                Sobre JUSTCLEAN VIC
              </h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Somos una empresa especializada en <strong>vaciado de pisos y locales en Vic</strong>, con más de una década de experiencia en <strong>limpieza por síndrome de Diógenes</strong> y <strong>suciedad extrema</strong>. Nuestro compromiso es ofrecer un servicio profesional, discreto y eficiente en toda la comarca de Osona. Conoce todos nuestros <Link to="/servicios" className="text-primary hover:text-red-700 font-semibold">servicios especializados</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* Nuestra Historia */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-dark mb-6">Nuestra historia</h2>
                <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                  <p>
                    JUSTCLEAN VIC nació hace más de 10 años con una misión clara: ofrecer soluciones profesionales de <strong>vaciado de pisos y locales</strong> en Vic y la comarca de Osona. Desde nuestros inicios, hemos trabajado con dedicación para convertirnos en la empresa de referencia en servicios de limpieza especializada.
                  </p>
                  <p>
                    A lo largo de estos años, hemos vaciado más de 500 pisos, casas, locales comerciales y trasteros en zonas como el Centre Històric de Vic, El Remei, Horta Vermella, Estadi, Vic Sud, Serra-de-senferm, Caputxins, y en localidades cercanas como Manlleu, Torelló, Taradell, Tona, Gurb, Calldetenes, Roda de Ter y Folgueroles.
                  </p>
                  <p>
                    Nuestra experiencia en casos de <Link to="/servicios#diogenes" className="text-primary hover:text-red-700 font-semibold">síndrome de Diógenes</Link> y <Link to="/servicios#limpieza-extrema" className="text-primary hover:text-red-700 font-semibold">limpieza de suciedad extrema</Link> nos ha permitido desarrollar protocolos específicos que garantizan un trato respetuoso, discreto y profesional en situaciones delicadas. Entendemos la sensibilidad de estos casos y trabajamos con la máxima confidencialidad.
                  </p>
                  <p>
                    Hoy en día, JUSTCLEAN VIC es sinónimo de confianza, profesionalidad y eficiencia. Colaboramos regularmente con inmobiliarias, administradores de fincas, particulares y empresas que confían en nosotros para la <Link to="/servicios#retirada-muebles" className="text-primary hover:text-red-700 font-semibold">retirada de muebles y electrodomésticos</Link>, vaciado integral de espacios y limpieza profunda.
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://public.readdy.ai/ai/img_res/d43df5d23a07d34a80b5b586ce709fd6.jpg"
                  alt="Equipo profesional de JUSTCLEAN VIC"
                  className="w-full h-auto rounded-lg shadow-xl object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Valores */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-dark mb-12 text-center">Nuestros valores</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-primary rounded-full mx-auto mb-4">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-dark mb-3">Profesionalidad</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Equipo cualificado con formación específica en vaciado de pisos, limpieza extrema y gestión de residuos.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-primary rounded-full mx-auto mb-4">
                  <i className="ri-eye-off-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-dark mb-3">Discreción</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Máxima confidencialidad en todos nuestros servicios, especialmente en casos de síndrome de Diógenes.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-primary rounded-full mx-auto mb-4">
                  <i className="ri-time-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-dark mb-3">Rapidez</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Servicio urgente disponible 24 horas. Respondemos de inmediato a tus necesidades.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-primary rounded-full mx-auto mb-4">
                  <i className="ri-hand-heart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-dark mb-3">Compromiso</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Garantizamos resultados óptimos y la satisfacción total de nuestros clientes.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Cómo Trabajamos */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-dark mb-12 text-center">Nuestra forma de trabajar</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center bg-secondary/20 rounded-lg">
                      <i className="ri-user-heart-line text-2xl text-primary"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-dark mb-2">Trato personalizado</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Cada proyecto es único. Nos adaptamos a tus necesidades específicas y te ofrecemos soluciones a medida para el <Link to="/servicios#vaciado-pisos" className="text-primary hover:text-red-700 font-semibold">vaciado de tu piso o local en Vic</Link>.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center bg-secondary/20 rounded-lg">
                      <i className="ri-file-list-3-line text-2xl text-primary"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-dark mb-2">Presupuesto transparente</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Sin sorpresas ni costes ocultos. Te entregamos un presupuesto detallado y sin compromiso antes de iniciar cualquier trabajo. <Link to="/faq" className="text-primary hover:text-red-700 font-semibold">Consulta nuestras preguntas frecuentes</Link> sobre precios.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center bg-secondary/20 rounded-lg">
                      <i className="ri-recycle-line text-2xl text-primary"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-dark mb-2">Gestión legal de residuos</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Nos encargamos de la <strong>retirada de muebles y electrodomésticos</strong> y gestionamos todos los residuos de forma legal y responsable con el medio ambiente.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center bg-secondary/20 rounded-lg">
                      <i className="ri-team-line text-2xl text-primary"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-dark mb-2">Equipo especializado</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Nuestros profesionales están formados específicamente en <strong>limpieza por síndrome de Diógenes</strong> y casos de suciedad extrema, garantizando un trato respetuoso.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center bg-secondary/20 rounded-lg">
                      <i className="ri-tools-line text-2xl text-primary"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-dark mb-2">Equipamiento profesional</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Utilizamos maquinaria y productos de limpieza profesionales para garantizar resultados óptimos en la desinfección y limpieza profunda.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center bg-secondary/20 rounded-lg">
                      <i className="ri-checkbox-circle-line text-2xl text-primary"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-dark mb-2">Garantía de satisfacción</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      No finalizamos el trabajo hasta que estés completamente satisfecho. Tu tranquilidad es nuestra prioridad.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Colaboraciones */}
        <section className="py-16 bg-gradient-to-br from-secondary/10 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-dark mb-6">Colaboramos con profesionales</h2>
              <p className="text-base text-gray-600 max-w-3xl mx-auto">
                Trabajamos habitualmente con inmobiliarias, administradores de fincas, empresas de mudanzas y particulares en Vic, Manlleu, Torelló y toda la comarca de Osona.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-4">
                  <i className="ri-building-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-lg font-bold text-dark mb-3">Inmobiliarias</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Vaciado rápido de pisos para poner en venta o alquiler. Dejamos el espacio listo para visitas con opción de <Link to="/servicios#pintura-reparaciones" className="text-primary hover:text-red-700 font-semibold">pintura y pequeñas reparaciones</Link>.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-4">
                  <i className="ri-community-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-lg font-bold text-dark mb-3">Administradores de fincas</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Servicio discreto para vaciado de viviendas en comunidades. Coordinación con vecinos y gestión completa del proceso.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-4">
                  <i className="ri-user-3-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-lg font-bold text-dark mb-3">Particulares</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Atención personalizada para herencias, mudanzas, vaciado de trasteros o casos de síndrome de Diógenes con total confidencialidad.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-dark mb-6">
              ¿Quieres conocernos mejor?
            </h2>
            <p className="text-base text-gray-600 mb-8 leading-relaxed">
              Estaremos encantados de atenderte y resolver todas tus dudas sobre nuestros servicios de <strong>vaciado de pisos y locales en Vic</strong>. <Link to="/contacto" className="text-primary hover:text-red-700 font-semibold">Contáctanos</Link> o escríbenos por WhatsApp para un presupuesto sin compromiso.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+34602365996"
                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap text-sm inline-flex items-center justify-center"
              >
                <i className="ri-phone-line mr-2"></i>
                Llamar: 602 36 59 96
              </a>
              <Link
                to="/contacto"
                className="bg-dark text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap text-sm inline-flex items-center justify-center"
              >
                <i className="ri-mail-line mr-2"></i>
                Formulario de contacto
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
      <CookieBanner />
    </div>
  );
}
